import convert from "convert-units";
import round from "lodash/round";
import React from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import SvgIcon from "../../../parts/SvgIcon";
import ToolTip from "../../../parts/tooltip";
import CalendarTrendLegend from "./components/CalendarTrendLegend";

const UNITS = {
  metric: "kg",
  imperial: "lb",
};

const RPE_INTENSITY_COLORS = [
  "#2787F5",
  "#5BC500",
  "#FFD400",
  "#FF8800",
  "#ED0A34",
  "#FFFFFF",
];

function DaylyWorkoutHeader({
  activeUser,
  query,
  openQuickEditMode,
  handleRemove,
  // Set only when the day has a workout without a note: shows "+ Instructions".
  onAddWorkoutNote,
  // Set only when the day has exercises (same rule as week edit mode).
  onAddSection,
  dailyStats,
  activeDay,
}) {
  const { t } = useTranslation("dashboard");
  const unit = activeUser.units;
  let sessionRPE,
    readinessRate,
    intensityColor,
    bodyMeasurements,
    bodyWeight = null,
    stepCount = null;

  if (dailyStats) {
    sessionRPE = dailyStats.sessionRPE || null;

    intensityColor = sessionRPE
      ? RPE_INTENSITY_COLORS[Math.trunc(sessionRPE - 5)]
      : null;

    readinessRate = dailyStats.readiness
      ? Object.values(dailyStats.readiness).reduce((a, b) => a + b, 0)
      : null;

    bodyWeight = dailyStats.bodyWeight
      ? unit === "metric"
        ? dailyStats.bodyWeight
        : round(convert(dailyStats.bodyWeight).from("kg").to("lb"), 1)
      : null;

    bodyMeasurements = Boolean(dailyStats.bodyMeasurements);
    stepCount = Number(dailyStats.steps) || null;
  }

  const RPEProps = intensityColor
    ? {
        style: {
          color: intensityColor,
        },
      }
    : {};

  const { athlete, organizationCoach = undefined } = query;
  // Trend indicators default to on; only an explicit `false` (set by the
  // toggle) turns them off, so an unset query param still renders as active.
  const relTrendsOn = query.relTrends !== false;

  const organizationCoachParam = organizationCoach
    ? `&organizationCoach=${organizationCoach}`
    : "";
  const urlStart = athlete ? "/athlete-portal/metrics" : "/dashboard/metrics";
  const athleteParam = athlete ? `&athlete=${athlete}` : "";
  const urlEnd = athleteParam + organizationCoachParam;
  const formatedDate = activeDay.format("YYYY-MM-DD");

  return (
    <>
    <header>
      <hgroup>
        <a
          href={`${urlStart}/readiness-rate?view=day&date=${formatedDate}${urlEnd}`}
        >
          {t("calendar.dailyWorkoutHeader.readinessRate")}
          <span>{readinessRate ? `${readinessRate} / 25` : "--"}</span>
        </a>
        <a
          href={`${urlStart}/session-rpe?view=day&date=${formatedDate}${urlEnd}`}
        >
          {t("calendar.dailyWorkoutHeader.sessionRpe")}
          <span {...RPEProps}>{dailyStats ? dailyStats.sessionRPE : "--"}</span>
        </a>
        <a
          href={`${urlStart}/bodyweight?view=day&date=${formatedDate}${urlEnd}`}
        >
          {t("calendar.dailyWorkoutHeader.bodyweight")}
          <span>
            {bodyWeight ? `${round(bodyWeight, 1)}${UNITS[unit]}` : "--"}
          </span>
        </a>
        <a
          href={`${urlStart}/step-count?view=day&date=${formatedDate}${urlEnd}`}
        >
          {t("calendar.dailyWorkoutHeader.stepCount")}
          <span>{stepCount ? stepCount : "--"}</span>
        </a>
        <button
          type="button"
          className="trend-toggle"
          role="switch"
          aria-checked={relTrendsOn}
          onClick={() =>
            submit("/actions/refresh/?", {
              method: "GET",
              body: { relTrends: !relTrendsOn },
            })
          }
        >
          <span className="trend-toggle-track">
            <span className="trend-toggle-knob" />
          </span>
          {t("calendar.dailyWorkoutHeader.relTrends")}
        </button>
      </hgroup>

      <hgroup>
        <ToolTip
          id="quick-edit-workout"
          body={t("calendar.dailyWorkoutHeader.quickEdit")}
        >
          <a
            onClick={openQuickEditMode}
            href="#_"
            data-button-type="secondary"
            role="button"
            className="px-[0.35rem] h-8"
          >
            <SvgIcon src="/img/new-icons/edit-pencil.svg" />
          </a>
        </ToolTip>

        <ToolTip
          id="delete-workout"
          body={t("calendar.dailyWorkoutHeader.deleteWorkout")}
        >
          <a
            href="#_"
            onClick={handleRemove}
            data-button-type="secondary"
            role="button"
            className="px-[0.35rem] h-8"
          >
            <SvgIcon
              src="/img/new-icons/red-trash.svg"
              noFilterOnLight={true}
            />
          </a>
        </ToolTip>

        {onAddSection && (
          <ToolTip
            id="add-section"
            body={t("athlete-portal:exerciseEditor.addSection")}
          >
            <button
              type="button"
              onClick={onAddSection}
              data-button-type="secondary"
              className="px-[0.35rem] h-8"
            >
              <SvgIcon src="/img/new-icons/workout-sections-icon.svg" />
            </button>
          </ToolTip>
        )}

        {onAddWorkoutNote && (
          <button
            type="button"
            data-button-type="secondary"
            className="workout-note-add-button h-8"
            onClick={onAddWorkoutNote}
          >
            <SvgIcon src="/img/new-icons/add.svg" />
            {t("calendar.dailyWorkoutHeader.addWorkoutNote")}
          </button>
        )}

        <a
          data-button-type="primary"
          role="button"
          href={`/actions/refresh?modal[type]=calendar-add-exercise&day=${query.date}&insertTarget=&insertPosition=`}
        >
          <SvgIcon src="/img/new-icons/add.svg" noFilterOnLight={true} />
          {t("calendar.dailyWorkoutHeader.addExercise")}
        </a>
      </hgroup>
    </header>
    {relTrendsOn && <CalendarTrendLegend activeUser={activeUser} />}
    </>
  );
}

export default DaylyWorkoutHeader;
